import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const FavoritesContext = createContext(null);

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    throw new Error(
      "useFavorites must be used within FavoritesProvider"
    );
  }
  return ctx;
};

export const FavoritesProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading, setShowLoginModal } =
    useAuth();


  const [favorites, setFavorites] = useState(
    () => new Set()
  );


  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      setFavorites(new Set());
      return;
    }

    let mounted = true;

    fetch(`${BASE_URL}/enduser/wishlist/`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((ids) => {
        if (!mounted) return;

        if (Array.isArray(ids)) {
          setFavorites(new Set(ids));
        } else {
          setFavorites(new Set());
        }
      })
      .catch(() => {
        if (mounted) setFavorites(new Set());
      });

    return () => {
      mounted = false;
    };
  }, [authLoading, isAuthenticated]);


  const toggleFavorite = async (productId) => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    if (!productId) return;


    setFavorites((prev) => {
      const next = new Set(prev);
      next.has(productId)
        ? next.delete(productId)
        : next.add(productId);
      return next;
    });

    if (!isAuthenticated) return;

    try {
      const res = await fetch(
        `${BASE_URL}/enduser/wishlist/toggle/`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            product_id: productId,
          }),
        }
      );

      if (!res.ok) {
        throw new Error("Toggle failed");
      }
    } catch {

      setFavorites((prev) => {
        const next = new Set(prev);
        next.has(productId)
          ? next.delete(productId)
          : next.add(productId);
        return next;
      });
    }
  };

  const isFavorite = (productId) =>
    favorites.has(productId);

  return (
    <FavoritesContext.Provider
      value={{
        toggleFavorite,
        isFavorite,
        favoritesCount: favorites.size,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};
